import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const CreateRadioStep3 = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const draft = JSON.parse(localStorage.getItem('radioDraft') || '{}');
  const { name, genre, language, description } = draft;
  const [broadcastType, setBroadcastType] = useState('auto');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null); // radio renvoyée par le serveur

  const handleCreate = async () => {
    setError('');
    if (!name) {
      setError(t('createRadio.nameMissing'));
      return;
    }
    setCreating(true);
    try {
      // Provisionne une VRAIE station (peut prendre ~15-30s).
      const res = await api.post('/api/radios', { name, description, genre, language });
      localStorage.removeItem('radioDraft');
      // IMPORTANT : couper le spinner AVANT d'afficher le succès. Sinon `creating` reste
      // true et, comme le rendu teste `if (creating)` avant `if (created)`, l'écran de
      // succès n'est jamais atteint -> spinner infini malgré la création réussie (201).
      setCreating(false);
      setCreated(res.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setError(err?.response?.data?.message || t('createRadio.limitError'));
      } else if (status === 502 || status === 503) {
        setError(err?.response?.data?.message || t('createRadio.provisionError'));
      } else {
        setError(err?.response?.data?.message || t('createRadio.createError'));
      }
      setCreating(false);
    }
  };

  const handlePrevious = () => navigate('/creer-radio/step2');

  // Écran de provisionnement en cours
  if (creating) {
    return (
      <div className="max-w-2xl mx-auto py-10 px-4 pt-28 text-center">
        <div className="bg-white p-10 rounded-2xl shadow-md">
          <div className="w-14 h-14 mx-auto mb-6 rounded-full border-4 border-purple-200 border-t-purple-600 animate-spin" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('createRadio.creatingTitle')}</h2>
          <p className="text-gray-600">
            {t('createRadio.creatingDesc')}
          </p>
        </div>
      </div>
    );
  }

  // Écran de succès
  if (created) {
    return (
      <div className="max-w-2xl mx-auto py-10 px-4 pt-28 text-center">
        <div className="bg-white p-10 rounded-2xl shadow-md">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-green-100 text-green-600 flex items-center justify-center text-3xl">✓</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('createRadio.successTitle')}</h2>
          <p className="text-gray-600 mb-6">
            <span className="font-medium">{created.name}</span> {t('createRadio.successDesc')}
          </p>
          {created.listen_url && (
            <div className="bg-gray-50 rounded-xl p-4 mb-4 text-left">
              <p className="text-xs text-gray-500 mb-1">{t('createRadio.listenLink')}</p>
              <code className="text-sm text-purple-600 break-all">{created.listen_url}</code>
            </div>
          )}
          <p className="text-sm font-medium text-gray-800 mb-1">{t('createRadio.successNext')}</p>
          <p className="text-xs text-gray-500 mb-6">{t('createRadio.goLiveNote')}</p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Link
              to={`/radio/${created.id}`}
              className="bg-gradient-to-r from-purple-600 to-blue-500 text-white px-6 py-2 rounded-md hover:opacity-90 font-semibold"
            >
              {t('createRadio.uploadCta')}
            </Link>
            <Link to="/mes-radios" className="bg-gray-100 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-200">
              {t('nav.myRadios')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-10 px-4 pt-28">
      <div className="text-center mb-10">
        <h1 className="text-3xl md:text-4xl font-bold mb-2 text-gray-900">{t('createRadio.title')}</h1>
        <p className="text-gray-500 mb-6">{t('createRadio.subtitle')}</p>
        <div className="flex justify-center items-center gap-6">
          {[1, 2, 3].map((step) => (
            <React.Fragment key={step}>
              <div className={`w-9 h-9 flex items-center justify-center rounded-full text-white font-semibold ${step === 3 ? 'bg-gradient-to-r from-purple-500 to-blue-500' : 'bg-gray-200 text-gray-600'}`}>{step}</div>
              {step < 3 && <div className="w-10 h-px bg-gray-300" />}
            </React.Fragment>
          ))}
        </div>
        <div className="flex justify-center mt-2 gap-12 text-sm">
          <span className="text-gray-500">{t('createRadio.stepInfo')}</span>
          <span className="text-gray-500">{t('createRadio.stepCustom')}</span>
          <span className="text-purple-600 font-semibold">{t('createRadio.stepConfig')}</span>
        </div>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-md">
        <h2 className="text-xl font-semibold mb-4">{t('createRadio.chooseBroadcast')}</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {[
            { id: 'live', label: t('createRadio.broadcast.liveLabel'), desc: t('createRadio.broadcast.liveDesc') },
            { id: 'auto', label: t('createRadio.broadcast.autoLabel'), desc: t('createRadio.broadcast.autoDesc') },
            { id: 'mixed', label: t('createRadio.broadcast.mixedLabel'), desc: t('createRadio.broadcast.mixedDesc') },
          ].map(({ id, label, desc }) => (
            <div
              key={id}
              onClick={() => setBroadcastType(id)}
              className={`border rounded-xl p-4 text-center cursor-pointer transition hover:shadow-md ${
                broadcastType === id ? 'border-purple-500 bg-purple-50' : 'border-gray-200'
              }`}
            >
              <h3 className="font-bold text-lg text-gray-900 mb-1">{label}</h3>
              <p className="text-sm text-gray-500">{desc}</p>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-blue-50 p-6 rounded-xl mb-6">
          <h3 className="text-lg font-semibold mb-2">{t('createRadio.summary')}</h3>
          <p><span className="font-medium">{t('createRadio.summaryName')}</span> {name || '-'}</p>
          <p><span className="font-medium">{t('createRadio.summaryGenre')}</span> {genre || '-'}</p>
          <p><span className="font-medium">{t('createRadio.summaryLanguage')}</span> {language || '-'}</p>
          <p><span className="font-medium">{t('createRadio.summaryType')}</span> {t(`createRadio.broadcast.${broadcastType}Label`)}</p>
        </div>

        {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

        <div className="flex justify-between">
          <button onClick={handlePrevious} className="px-6 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100">{t('createRadio.previous')}</button>
          <button
            onClick={handleCreate}
            disabled={creating}
            className="px-6 py-2 rounded-md text-white font-semibold bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 disabled:opacity-60"
          >
            {creating ? t('createRadio.creating') : t('createRadio.createMyRadio')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateRadioStep3;
